import type { PImage } from '../../engine/aliases'
import type { IEntity } from '../../entities/entitiesUtils'
import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import { angleUtil } from '../../lib/angleUtil-sidecar'
import type { IXY } from '../../lib/distanceUtil'
import { distanceUtil } from '../../lib/distanceUtil-sidecar'
import { l } from '../../lib/imports'
import { rando } from '../../lib/rando-sidecar'
import { createId } from '../../main/common'
import { common } from '../../main/common-sidecar'
import { sprites } from '../../sprites/sprites-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import {
  EMessageBulletType,
  IVoyageMessageBulletCreate,
} from './multi/voyageMessageBulletCreate'
import { EVoyageCrewDamageType } from './multi/voyageMessageCrewHurt'
import { voyageColors } from './voyageColors-sidecar'
import { EVoyageCrewType, IVoyageCrew } from './voyageCrew'
import { voyageCrew } from './voyageCrew-sidecar'
import type { IVoyageCrewWeaponData } from './voyageCrewWeaponData'
import { voyageCrewWeaponData } from './voyageCrewWeaponData-sidecar'
import { voyageLayers } from './voyageLayers-sidecar'
import { voyageParticles } from './voyageParticles-sidecar'
import { voyageSounds } from './voyageSounds-sidecar'
import { voyageSplatters } from './voyageSplatters-sidecar'
import { voyageTilemap } from './voyageTilemap-sidecar'

export interface IVoyageBullet extends IEntity {
  data: IVoyageCrewWeaponData
  image: PImage
  x: number
  y: number
  team: number
  // tx: number
  // ty: number
  rotation: number
  dx: number
  dy: number
  firedCrewId: number
}

const objs: IVoyageBullet[] = []
export function getAll() {
  return objs
}

export function shoot(
  team: number,
  data: IVoyageCrewWeaponData,
  start: IXY,
  end: IXY,
  lead: number,
  firedCrewId: number,
): IVoyageMessageBulletCreate {
  let rot = angleUtil.calcAngleToXY(start, end)
  let dx = Math.cos(rot)
  let dy = Math.sin(rot)
  let ox = dx * lead
  let oy = dy * lead

  let msg: IVoyageMessageBulletCreate = {
    bulletId: createId(),
    firedCrewId: firedCrewId,
    rot,
    team,
    x: start.x + ox,
    y: start.y + oy,
    type: data.type,
    dx,
    dy,
  }
  return msg
}

export function create(msg: IVoyageMessageBulletCreate): IVoyageBullet {
  // console.log('create bullet')
  let data =
    msg.type === EMessageBulletType.Alien
      ? voyageCrewWeaponData.getAlienBlaster()
      : voyageCrewWeaponData.getCrewBlaster()

  let frame = sprites.voyTileParticlesRound[2]
  let image = spritesUtil.createImage(voyageLayers.getLayers().shipCrew, frame)
  image.tint = data.color

  let id = createId()

  let obj: IVoyageBullet = {
    id,
    idLoaded: -1,
    isDead: false,
    eType: 'voy-misc',
    image,
    data,
    x: msg.x,
    y: msg.y,
    team: msg.team,
    rotation: msg.rot,
    dx: msg.dx,
    dy: msg.dy,
    firedCrewId: msg.firedCrewId,
  }
  objs.push(obj)

  moveToXY(obj, obj.x, obj.y)

  if (obj.team === 0) {
    voyageSounds.playShootCrewGun()
  } else {
    voyageSounds.playShootAlienGun()
  }

  return obj
}

export function moveToXY(obj: IVoyageBullet, x: number, y: number) {
  obj.x = x
  obj.y = y
  obj.image.x = x
  obj.image.y = y
}

export function kill(obj: IVoyageBullet) {
  obj.isDead = true
}
export function killAll() {
  l.forEach(objs, (c) => {
    kill(c)
  })
  removeDead()
}

export function updateAll() {
  l.forEach(objs, (c) => {
    update(c)
  })
  removeDead()
}
export function removeDead() {
  entitiesUtils.removeDead(objs, (c) => {
    c.image.destroy()
  })
}
function update(obj: IVoyageBullet) {
  let ds = common.getUpdateDeltaScaled()

  if (obj.isDead) {
    return
  }

  let speed = obj.data.speed

  let ox = obj.dx * speed * ds
  let oy = obj.dy * speed * ds

  moveToXY(obj, obj.x + ox, obj.y + oy)

  let curT = voyageTilemap.getSafeXY(obj)
  if (!curT) {
    kill(obj)
  } else {
    if (!curT.tile2.canWalk) {
      kill(obj)
    }
  }

  if (obj.isDead) {
    return
  }

  let tgs = voyageCrew.getAll()
  let closestTarget: IVoyageCrew | null = null
  let closestDistance = 16
  for (let i = 0; i < tgs.length; i++) {
    let target = tgs[i]
    if (target.isDead || target.isDying) {
      continue
    }
    if (target.team === -1) {
      continue
    }
    if (target.team === obj.team) {
      continue
    }

    let dist = distanceUtil.distanceNormalXY(obj, target)
    if (dist < closestDistance) {
      closestDistance = dist
      closestTarget = target
    }
  }
  if (closestTarget) {
    kill(obj)
    if (closestTarget.type === EVoyageCrewType.Alien) {
      voyageParticles.createMultiple('blood-alien', obj.image.x, obj.image.y, 3)
      voyageSplatters.createMultiple(
        rando.i(1, 2),
        obj.image.x,
        obj.image.y,
        voyageColors.greenDull,
      )
    }
    if (closestTarget.type === EVoyageCrewType.Crew) {
      voyageParticles.createMultiple('blood-crew', obj.image.x, obj.image.y, 3)
      voyageSplatters.createMultiple(
        rando.i(1, 2),
        obj.image.x,
        obj.image.y,
        voyageColors.redDull,
      )
    }
    voyageCrew.hurt(
      closestTarget,
      obj.data.damage,
      EVoyageCrewDamageType.SmallBullet,
      obj.firedCrewId,
    ) // 'Gunshot wound')
  }

  // let dx =

  // Lerp
}
